// =============================================================================
// App.js — Root component: routing, navigation sidebar, and access-mode logic
// =============================================================================
// This file owns:
//   • Three access modes:  'normal' (SSO user), 'store' (HubSpot popup), 'admin'
//   • The sidebar navigation with NavLink items
//   • The central React Router <Routes> that maps URLs to page components
//
// HOW TO ADD A NEW PAGE:
//   1. Create the page component in client/src/pages/MyNewPage.js
//   2. Import it at the top of this file
//   3. Add a <NavLink> entry inside the <nav className="sidebar"> block
//   4. Add a <Route path="/my-page" element={<MyNewPage />} /> inside <Routes>
// =============================================================================
import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import './App.css';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderLinesEditorPage from './pages/OrderLinesEditorPage';
import PrintOrderPage from './pages/PrintOrderPage';
import ProductsPage from './pages/ProductsPage';
// PacksPage removed — packs are now shown in the Products page
import DashboardPage from './pages/DashboardPage';
import IntegrationsPage from './pages/IntegrationsPage';
import SchedulerPage from './pages/SchedulerPage';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';

// -----------------------------------------------------------------------------
// getAccessMode — reads URL params BEFORE Router mounts to decide render path.
// Called once per page load; result drives which branch of AppContent renders.
// -----------------------------------------------------------------------------
function getAccessMode() {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  if (path === '/order-editor' && params.get('CentralAdmin') === 'true' && params.get('code') === '2026') {
    return 'admin';   // full sidebar, no SSO — used by central office staff via direct URL
  }
  if (path === '/order-editor' && params.get('p') === 'true') {
    return 'print';   // print/PDF view — picking list + dispatch note, no sidebar
  }
  if (path === '/order-editor' && (params.get('hubspotOrderId') || params.get('orderId') || params.get('storeId'))) {
    return 'store';   // no sidebar, editor only — opened from HubSpot or store bookmark
  }
  return 'normal';    // authenticated user, full sidebar + all routes
}

// -----------------------------------------------------------------------------
// AppContent — top-level render controller
// Checks access mode first, then handles auth loading/login states.
// -----------------------------------------------------------------------------
function AppContent() {
  const { user, loading, logout } = useAuth();
  const accessMode = getAccessMode();

  // Print mode: show only the PrintOrderPage — no sidebar, no auth.
  // Triggered by p=true in the URL; fetches order and auto-opens print dialog.
  if (accessMode === 'print') {
    return (
      <Router>
        <div style={{ minHeight: '100vh', background: '#f0f0f0' }}>
          <Routes>
            <Route path="/order-editor" element={<PrintOrderPage />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // Store-only mode: skip auth entirely, show ONLY the order editor (no sidebar).
  // This is the mode used when a store opens the portal via a HubSpot card or bookmark.
  if (accessMode === 'store') {
    return (
      <Router>
        <div style={{ height: '100vh', overflow: 'auto', background: '#f8fafc' }}>
          <Routes>
            {/* Only one route is exposed in store mode */}
            <Route path="/order-editor" element={<OrderLinesEditorPage />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // Admin mode: skip auth, show full sidebar + all routes.
  // Useful for central admins who need access without an Azure AD account.
  if (accessMode === 'admin') {
    return (
      <Router>
        <FullApp user={{ displayName: 'Central Admin', email: 'admin' }} logout={() => { window.location.href = '/'; }} />
      </Router>
    );
  }

  // Normal authenticated mode — show spinner while session is being verified
  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0f172a', color:'#94a3b8', fontFamily:'sans-serif' }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ width:40, height:40, border:'3px solid #334155', borderTop:'3px solid #3b82f6', borderRadius:'50%', margin:'0 auto 16px', animation:'spin 1s linear infinite' }}></div>
          Loading…
        </div>
      </div>
    );
  }

  // Not logged in → show the Azure AD login page
  if (!user) {
    return <LoginPage />;
  }

  // Fully authenticated normal user
  return (
    <Router>
      <FullApp user={user} logout={logout} />
    </Router>
  );
}

// -----------------------------------------------------------------------------
// FullApp — renders the sidebar + main content area for normal/admin mode.
// user.displayName and user.email are shown in the bottom sidebar widget.
// logout() triggers the Azure AD sign-out flow via AuthContext.
// -----------------------------------------------------------------------------
function FullApp({ user, logout }) {
  return (
      <div className="app-layout">
        {/* ---- HubSpot-style left sidebar ---- */}
        {/* To add a new nav item: copy a <NavLink> block and update path + icon */}
        <nav className="sidebar">
          <div className="sidebar-logo" title="Oakhouse Foods Orders Portal" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 4px 8px' }}>
            <img
              src="/oakhouse-logo.png"
              alt="Oakhouse Foods"
              style={{ width: 48, height: 48, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="sidebar-nav">
            {/* Dashboard — analytics overview */}
            <NavLink to="/" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`} end>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              <span>Dashboard</span>
            </NavLink>
            {/* Orders — list of all orders, create new orders */}
            <NavLink to="/orders" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              <span>Orders</span>
            </NavLink>
            {/* Order Editor — full product picker and line editor */}
            <NavLink to="/order-editor" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              <span>Order Editor</span>
            </NavLink>
            {/* Products — catalogue browser with stock levels and substitution tools */}
            <NavLink to="/products" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
              <span>Products</span>
            </NavLink>

            {/* Divider before admin/ops pages */}
            <div className="sidebar-divider" style={{height:1,background:'#334155',margin:'8px 12px'}}></div>
            {/* Integrations — manage Pimberly, On-Prem SQL, HubSpot connections */}
            <NavLink to="/integrations" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              <span>Integrations</span>
            </NavLink>
            {/* Scheduler — view and control automated background sync tasks */}
            <NavLink to="/scheduler" className={({isActive}) => `sidebar-item ${isActive ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
              <span>Scheduler</span>
            </NavLink>
          </div>

          {/* User profile / logout at bottom of sidebar */}
          <div className="sidebar-user">
            <div className="sidebar-user-info">
              <div className="sidebar-avatar" title={user.email}>
                {/* First letter of display name as avatar */}
                {(user.displayName || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="sidebar-user-details">
                <span className="sidebar-user-name">{user.displayName}</span>
                <span className="sidebar-user-email">{user.email}</span>
              </div>
            </div>
            <button className="sidebar-logout" onClick={logout} title="Sign out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </nav>

        {/* ---- Main content area — routes render here ---- */}
        {/* To add a new page: add a <Route> here and a matching NavLink above */}
        <div className="main-content">
          <Routes>
            <Route path="/" element={<DashboardPage />} />               {/* Analytics & performance */}
            <Route path="/orders" element={<OrdersPage />} />             {/* Order list & create */}
            <Route path="/orders/:id" element={<OrderDetailPage />} />    {/* Order detail (legacy view) */}
            <Route path="/order-editor" element={<OrderLinesEditorPage />} /> {/* Main order editing experience */}
            <Route path="/products" element={<ProductsPage />} />         {/* Product catalogue */}

            <Route path="/integrations" element={<IntegrationsPage />} /> {/* Integration config & sync */}
            <Route path="/scheduler" element={<SchedulerPage />} />       {/* Scheduled task manager */}
          </Routes>
        </div>
      </div>
  );
}

// App wraps everything in AuthProvider so any child component can call useAuth()
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
